import { login as loginByUsersApi } from "@/api/users";
import {
  getUserInfo as getUserInfoByUsersApi,
  logout as logoutByUsersApi,
} from "@/api/users";
import type { ClassListItem, UserInfo } from "@/api/user";
import { resetRouter } from "@/router";
import { routePermission } from "@/permission";
import { store } from "@/store";
import { QB_TOKEN_KEY, TOKEN_KEY } from "@/enums/CacheEnum";
import { getQuestionBankToken } from "@/api/questionBank";
import { useStorage } from "@vueuse/core";
import md5 from "md5";
import type { RequestErrorHandler } from "@/types/request";

interface LoginData {
  username: string;
  password: string;
  captchaKey?: string;
  captchaCode?: string;
}

interface UsersApiLoginResult {
  token?: string;
  tokenType?: string;
  accessToken?: string;
}

interface QuestionBankTokenResponse {
  // 少数部署会直接返回令牌
  accessToken?: string;
  // 标准响应通过 data 包裹业务结果，兼容历史双层 data 结构
  data?: {
    // 标准题库令牌字段
    accessToken?: string;
    // 历史代理可能再次包裹 data
    data?: {
      // 双层 data 响应中的题库令牌
      accessToken?: string;
    };
  };
}

export const useUserStore = defineStore("user", () => {
  const user = ref<UserInfo>({
    roles: [],
    perms: [],
  });
  /** 当前选中的学校（与 itestaiclassroom 一致，持久化；空则回退 user.schoolIds[0]） */
  const schoolId = useStorage<string>(
    "schoolId",
    (user.value.schoolIds?.[0] ?? "") as string
  );
  /** 班级列表（由 getUserInfo 的 classNames 填充，持久化） */
  const classList = useStorage<ClassListItem[]>("classList", []);
  // 题库访问令牌，同时与 localStorage 保持一致，供跟读页面和请求拦截器共用
  const questionBankToken = ref<string>(
    localStorage.getItem(QB_TOKEN_KEY) || ""
  );

  /** 登出 / token 失效时清理与用户绑定的持久化数据 */
  function clearUserSessionData() {
    schoolId.value = "";
    classList.value = [];
    user.value = { roles: [], perms: [] };
  }

  /** 将持久化 schoolId 与当前用户信息对齐（过期或不在列表中则回退） */
  function syncSchoolId(userInfo: UserInfo) {
    const schoolIds = userInfo.schoolIds ?? [];
    const fallback = (schoolIds[0] || userInfo.schoolId || "") as string;

    if (!schoolId.value) {
      schoolId.value = fallback;
      return;
    }

    if (schoolIds.length > 0 && !schoolIds.includes(schoolId.value)) {
      schoolId.value = fallback;
    }
  }

  /**
   * 登录
   *
   * @param {LoginData}
   * @returns
   */
  function login(loginData: LoginData, onError?: RequestErrorHandler) {
    return new Promise<void>((resolve, reject) => {
      const payload = {
        ...loginData,
        username: loginData.username.trim(),
        password: loginData.password.trim(),
      };

      loginByUsersApi(payload, onError)
        .then(async (data: any) => {
          const loginResult: UsersApiLoginResult = data?.data || data || {};
          const token =
            loginResult.token ||
            (loginResult.tokenType && loginResult.accessToken
              ? `${loginResult.tokenType} ${loginResult.accessToken}`
              : "");
          if (!token) {
            reject(new Error("登录成功但未返回 token"));
            return;
          }
          localStorage.setItem(TOKEN_KEY, token);
          try {
            await tryFetchQuestionBankToken();
          } catch (error) {
            // 题库 token 获取失败不阻断主登录流程
            console.warn("Failed to fetch question bank token:", error);
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // 获取信息(用户昵称、头像、角色集合、权限集合)
  function getUserInfo() {
    return new Promise<UserInfo>((resolve, reject) => {
      getUserInfoByUsersApi()
        .then((res: any) => {
          const data = (res?.data || res || {}) as UserInfo;
          if (!Object.keys(data).length) {
            reject("Verification failed, please Login again.");
            return;
          }
          if (!data.roles || data.roles.length <= 0) {
            reject("getUserInfo: roles must be a non-null array!");
            return;
          }
          const userInfo = { ...data, perms: data.perms || [] } as UserInfo;
          Object.assign(user.value, userInfo);

          if (userInfo.classNames?.length) {
            classList.value = userInfo.classNames.map((item) => ({
              ...item,
              id: md5(
                `${item.gradeNo ?? ""}-${encodeURIComponent(item.className)}`
              ),
            }));
          }
          syncSchoolId(userInfo);

          resolve(userInfo);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // user logout
  function logout() {
    return new Promise<void>((resolve, reject) => {
      // 跳过登出接口调用，直接执行清理逻辑
      localStorage.setItem(TOKEN_KEY, "");
      localStorage.setItem(QB_TOKEN_KEY, "");
      questionBankToken.value = "";
      clearUserSessionData();
      routePermission.reset();
      location.reload(); // 清空路由
      resolve();
    });
  }

  // remove token
  function resetToken() {
    return new Promise<void>((resolve) => {
      localStorage.setItem(TOKEN_KEY, "");
      localStorage.setItem(QB_TOKEN_KEY, "");
      questionBankToken.value = "";
      clearUserSessionData();
      routePermission.reset();
      resetRouter();
      resolve();
    });
  }

  /**
   * 登录后按部署环境预取题库令牌；失败不会阻断主登录流程
   * @returns 获取到的令牌；当前环境无需预取时返回空字符串
   */
  async function tryFetchQuestionBankToken(): Promise<string> {
    const shouldFetchQBankToken =
      window.location.href.includes("https://b.szshipian.com") ||
      window.location.href.includes("http://192.168.0.2") ||
      import.meta.env.DEV;
    if (!shouldFetchQBankToken) {
      return "";
    }

    return fetchQuestionBankToken();
  }

  /**
   * 获取并缓存单词跟读所需的题库访问令牌
   * @returns 可供题库接口直接使用的访问令牌
   */
  async function fetchQuestionBankToken(): Promise<string> {
    const appId = import.meta.env.VITE_APP_QBANK_ID;
    const appSecret = import.meta.env.VITE_APP_QBANK_SECRET;
    if (!appId || !appSecret) {
      throw new Error(
        "VITE_APP_QBANK_ID or VITE_APP_QBANK_SECRET is not defined"
      );
    }

    const response = (await getQuestionBankToken({
      appId,
      appSecret,
      expiresIn: 600000,
    })) as unknown as QuestionBankTokenResponse;
    const accessToken =
      response?.data?.accessToken ||
      response?.data?.data?.accessToken ||
      response?.accessToken ||
      "";
    if (!accessToken) {
      throw new Error("题库令牌响应中缺少 accessToken");
    }

    localStorage.setItem(QB_TOKEN_KEY, accessToken);
    questionBankToken.value = accessToken;
    return accessToken;
  }

  return {
    user,
    /** 与前台组件命名对齐，等价于 `user` */
    userInfo: user,
    schoolId,
    classList,
    questionBankToken,
    fetchQuestionBankToken,
    login,
    getUserInfo,
    logout,
    resetToken,
  };
});

/**
 * 用于在组件外部（如在Pinia Store 中）使用 Pinia 提供的 store 实例。
 * 官方文档解释了如何在组件外部使用 Pinia Store：
 * https://pinia.vuejs.org/core-concepts/outside-component-usage.html#using-a-store-outside-of-a-component
 */
export function useUserStoreHook() {
  return useUserStore(store);
}
